/**
 * Freehand Drawing Tool - Main Tool Logic
 * State machine for handling drawing operations with pointer events
 */

import { vec2, snapAngle, snapToOrthogonal, snapToGrid } from './geometry';
import { smoothPath } from './smoothing';

export class FreehandTool {
  constructor(config) {
    this.state = { kind: 'idle' };
    this.modifiers = { ctrl: false, shift: false, alt: false };
    this.config = config;
    this.paths = new Map();
    this.eventListeners = [];
    this.lastSampleTime = 0;
    this.previewPath = null;
    this.setupKeyboardListeners();
  }

  // ============================================================================
  // Public API
  // ============================================================================

  onPointerDown(point) {
    const worldPoint = this.applySnapping(point);

    if (this.state.kind === 'idle') {
      this.startNewPath(worldPoint);
    }
    // For true freehand drawing, we don't handle additional pointer downs while drawing
  }

  onPointerMove(point) {
    const worldPoint = this.applySnapping(point);

    if (this.state.kind === 'drawing') {
      this.handleFreehandDrawing(worldPoint);
    }
  }

  onPointerUp(point) {
    const worldPoint = this.applySnapping(point);

    if (this.state.kind === 'drawing') {
      this.finalizePath();
    }
  }

  onDoubleClick(point) {
    if (this.state.kind === 'drawing') {
      this.finalizePath();
    }
  }

  onKeyDown(event) {
    this.updateModifiers(event);
    
    switch (event.key) {
      case 'Enter':
        if (this.state.kind === 'drawing') {
          this.finalizePath();
        }
        break;
      case 'Escape':
        if (this.state.kind === 'drawing') {
          this.cancelPath();
        }
        break;
    }
  }

  onKeyUp(event) {
    this.updateModifiers(event);
  }

  addEventListener(listener) {
    this.eventListeners.push(listener);
  }

  removeEventListener(listener) {
    const index = this.eventListeners.indexOf(listener);
    if (index >= 0) {
      this.eventListeners.splice(index, 1);
    }
  }

  getPath(pathId) {
    return this.paths.get(pathId);
  }

  getAllPaths() {
    return Array.from(this.paths.values());
  }

  getPreviewPath() {
    return this.previewPath;
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  reset() {
    this.state = { kind: 'idle' };
    this.paths.clear();
    this.previewPath = null;
  }

  // ============================================================================
  // Private Methods - Path Management
  // ============================================================================

  startNewPath(point) {
    // Validate point
    if (!point || typeof point.x === 'undefined' || typeof point.y === 'undefined') {
      console.warn('Invalid point in startNewPath:', point);
      return;
    }

    const pathId = this.generatePathId();

    // Start with empty path - we'll build it as we draw
    const newPath = {
      id: pathId,
      nodes: [],
      segments: [],
      closed: false,
      stroke: { ...this.config.defaultStroke },
      layer: 0,
      visible: true,
      locked: false
    };

    this.paths.set(pathId, newPath);

    this.state = {
      kind: 'drawing',
      pathId,
      sketch: [{ x: point.x, y: point.y }], // Start collecting points
      startTime: Date.now()
    };

    this.emitEvent({
      type: 'pathStarted',
      pathId,
      path: newPath
    });
  }

  handleFreehandDrawing(point) {
    if (this.state.kind !== 'drawing') return;

    // Validate point
    if (!point || typeof point.x === 'undefined' || typeof point.y === 'undefined') {
      console.warn('Invalid point in handleFreehandDrawing:', point);
      return;
    }

    // Ensure sketch array exists
    if (!this.state.sketch || this.state.sketch.length === 0) {
      this.state.sketch = [point];
      return;
    }

    const lastPoint = this.state.sketch[this.state.sketch.length - 1];

    // Safety check for lastPoint
    if (!lastPoint || typeof lastPoint.x === 'undefined' || typeof lastPoint.y === 'undefined') {
      this.state.sketch = [point];
      return;
    }

    const distance = vec2.distance(point, lastPoint);

    // Add point if it's far enough from the last point
    if (distance >= (this.config.smoothing.minDistance || 2)) {
      this.state.sketch.push(point);
      this.updatePreviewPath();
    }
  }

  // Simplified finalize method for true freehand drawing

  finalizePath() {
    if (this.state.kind !== 'drawing') return;

    const path = this.paths.get(this.state.pathId);
    if (!path || !this.state.sketch || this.state.sketch.length < 2) {
      // Cancel if no valid path
      this.cancelPath();
      return;
    }

    // Apply smoothing to the collected points
    const smoothingConfig = this.modifiers.alt
      ? { ...this.config.smoothing, tolerance: this.config.smoothing.tolerance * 0.5 }
      : this.config.smoothing;

    const result = smoothPath(this.state.sketch, smoothingConfig);

    // Update the path with the smoothed result
    path.nodes = result.nodes;
    path.segments = result.segments;

    this.emitEvent({
      type: 'pathFinished',
      pathId: this.state.pathId,
      path
    });

    // Clear everything
    this.state = { kind: 'idle' };
    this.previewPath = null;
    this.paths.delete(this.state.pathId); // Remove from internal storage since it's now a template element
  }

  cancelPath() {
    if (this.state.kind !== 'drawing') return;

    const pathId = this.state.pathId;
    this.paths.delete(pathId);

    this.emitEvent({
      type: 'pathCancelled',
      pathId
    });

    this.state = { kind: 'idle' };
    this.previewPath = null;
  }

  // ============================================================================
  // Private Methods - Preview and Snapping
  // ============================================================================

  updatePreviewPath() {
    if (this.state.kind !== 'drawing' || !this.state.sketch || this.state.sketch.length < 2) return;

    const path = this.paths.get(this.state.pathId);
    if (!path) return;

    // Create a temporary preview of the current sketch
    const result = smoothPath(this.state.sketch, this.config.smoothing);

    this.previewPath = {
      id: 'preview',
      nodes: result.nodes,
      segments: result.segments,
      closed: false,
      stroke: { ...path.stroke, opacity: 0.7 },
      layer: 1000 // High layer for preview
    };
  }

  applySnapping(point) {
    if (!this.config.snap.enabled) return point;

    let snappedPoint = point;

    // Grid snapping
    if (this.config.snap.grid) {
      snappedPoint = snapToGrid(snappedPoint, 10); // TODO: make grid size configurable
    }

    // TODO: Add object snapping (endpoints, intersections, etc.)

    return snappedPoint;
  }

  // ============================================================================
  // Private Methods - Utilities
  // ============================================================================

  setupKeyboardListeners() {
    // Note: In a real implementation, these would be managed by the parent component
    // to avoid conflicts with other tools
  }

  updateModifiers(event) {
    this.modifiers = {
      ctrl: event.ctrlKey || event.metaKey,
      shift: event.shiftKey,
      alt: event.altKey
    };
  }

  generatePathId() {
    return `path_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  generateNodeId() {
    return `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  emitEvent(event) {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in freehand event listener:', error);
      }
    });
  }
}
